import type {
  AgenticLogicManagedBy,
  AgenticLogicRuleDraftDto,
  AgenticLogicTemplateSummaryDto,
  UserInput,
  UserInputAttachedFile,
} from "@intelligems/ig-types";
import {
  renderAgenticLogicRequestContract,
  renderAgenticLogicRequestFieldNotes,
  renderAgenticLogicSdkFieldNotes,
} from "@intelligems/ig-types";
import {
  AGENTIC_LOGIC_SDK_AMBIENT_DTS,
  AGENTIC_LOGIC_STARTER_SOURCE,
  type AgenticLogicDataSurface,
  buildAgenticLogicTypesModuleSource,
} from "@intelligems/ig-utils";

import { AGENTIC_CONTROLS_PANEL } from "@src/features/ai/pages/agentic-logic/workspace/agenticDomain";

interface AgenticLogicCodeEntryBase {
  kind: "code";
  initialPrompt: string;
  runtimeContract: string;
  /**
   * Per-surface availability of the request context, rendered from the one
   * table in ig-types. The authoring prompt interpolates it so the agent
   * cannot propose a rule keyed on data its chosen surfaces never send.
   */
  requestContract: string;
  sdkTypes: string;
  /**
   * Whether the author may use datasets and Sources — the same answer the
   * `sdkTypes` projection was built from. The authoring prompt renders its
   * dataset and Source guidance only when this is true. It is documentation,
   * not access: the routes check the caller's own scope.
   */
  dataSurfaceEnabled: boolean;
}

/**
 * The code entry is a discriminated PAIR, not one shape with optional fields:
 * a create session carries the blank scaffold and an edit session carries the
 * rule as it stands. Splitting them is what makes it impossible to build an
 * edit context without the rule's draft — the same "required rather than
 * defaulting, so a new call site cannot silently reintroduce the bad
 * projection" rationale as `dataSurface` below.
 *
 * Before this split both variants rendered `starterSource`, so an edit handed
 * the model an empty scaffold and NO representation of the rule it was
 * editing; the only move that path afforded was regenerating the whole file
 * from the rule's name and description, silently dropping every filter and
 * ordering the merchant never asked about.
 */
export type AgenticLogicCreationEntry =
  | (AgenticLogicCodeEntryBase & {
      existingRuleId?: undefined;
      starterSource: string;
    })
  | (AgenticLogicCodeEntryBase & {
      /**
       * Set when the session opens from an existing rule's workspace rather
       * than the create page: the idle Atlas rail is the SAME chat there, so
       * without this the agent would read a "run this cart" or "change the
       * fallback" opener as a brief for a brand-new rule.
       */
      existingRuleId: string;
      /**
       * Whether that rule is configured from a first-party template rather
       * than authored as code. The rail is always visible on EVERY rule's
       * page, and every one of them opens a code-entry session, so without
       * this flag a template rule's "only recommend from the Sale collection"
       * reads as an authoring brief: the agent writes a whole TypeScript rule
       * against an empty source and only discovers the mistake when
       * `save_agentic_logic_rule_draft` refuses it ("Builder rules have no
       * code workspace"). A template rule's logic lives in its parameters,
       * which `update_agentic_logic_rule` can change.
       */
      existingRuleIsTemplate: boolean;
      /**
       * The blank code scaffold, rendered ONLY for a template rule's session.
       *
       * A template rule's ask can outgrow its parameters — a variant strategy
       * the enum has no value for, a branch the schema cannot express — and
       * the answer is to rebuild it as a code rule in the same chat (the
       * prompt's eject path). That authoring starts from nothing, so it needs
       * the same scaffold the create page hands a new rule.
       *
       * Empty for a CODE rule, deliberately: there the stored source is
       * authoritative and the preservation contract forbids re-authoring from
       * a scaffold. Mustache reads the empty string as falsy, so the section
       * simply does not render.
       */
      ejectStarterSource: string;
      /**
       * The rule's merchant-facing copy as it stands: the name in the page
       * header and the description under it. Both are the agent's own writing
       * from the turn that created the rule, and both keep asserting whatever
       * that turn asserted — a threshold, a count, a collection — until
       * someone rewrites them. Without them in the session the agent edits the
       * source blind to the copy it is invalidating, which is how a rule ends
       * up named "Cheapest products to pass $100" over code that tests £700.
       *
       * Carried for a template rule too: a parameter change dates the copy
       * exactly as a source change does.
       */
      currentName: string;
      currentDescription: string;
      /**
       * The rule's draft source and graph, STAMPED WHEN THE SESSION OPENED —
       * `initialContext` is built once and re-sent verbatim on every turn, so
       * this snapshot goes stale the moment the model's own first edit lands.
       * The authoring node is also granted `get_agentic_logic_rule_draft` and
       * told to re-read before any save that is not the session's first; the
       * interpolation makes the common case unskippable, the tool makes the
       * snapshot refreshable. Neither half is redundant — do not "simplify"
       * either one away.
       *
       * Empty string means "no draft saved yet": mustache reads it as falsy,
       * which is the `{{#…}}/{{^…}}` idiom the prompt already uses.
       */
      currentSource: string;
      currentGraphJson: string;
    })
  | {
      kind: "template";
      template: AgenticLogicTemplateSummaryDto;
    };

export interface AgenticLogicCreationInitialContext {
  entry: AgenticLogicCreationEntry;
  initialInput: UserInput;
}

/**
 * The code-entry creation context, shared by the create-rule page's "Build"
 * flow and the always-visible Atlas panel's idle chat — both start a session
 * from nothing but a prompt, so both need the same runtime contract, starter
 * source, and SDK types.
 *
 * `dataSurface` is required and is the same answer the Monaco types module is
 * built from: off for a merchant, and for an Intelligems admin on with the
 * ORG's authored dataset keys. Dataset keys are org-scoped, not rule-scoped,
 * so "no rule exists yet" says nothing about what the org has authored; typing
 * them as an empty list projects `AgenticLogicDatasetKey = never`. It is
 * required rather than defaulting so a new call site must decide it from the
 * acting user instead of silently inheriting one audience's projection.
 *
 * `existingRule` is the edit selector: passing it (with the rule's draft, which
 * may be null on a rule that has never been saved) is the ONLY way to build an
 * edit context, and an edit context never carries `starterSource`.
 */
export const buildCodeEntryCreationContext = ({
  prompt,
  files,
  existingRule,
  dataSurface,
}: {
  prompt: string;
  files?: UserInputAttachedFile[];
  existingRule?: {
    ruleId: string;
    managedBy: AgenticLogicManagedBy;
    name: string;
    description: string;
    draft: AgenticLogicRuleDraftDto | null;
  };
  dataSurface: AgenticLogicDataSurface;
}): AgenticLogicCreationInitialContext => {
  const base: AgenticLogicCodeEntryBase = {
    kind: "code",
    initialPrompt: prompt,
    runtimeContract: AGENTIC_CONTROLS_PANEL.runtimeContract,
    // Every surface: at creation the rule has not chosen yet, and choosing is
    // the first thing the agent does.
    requestContract: renderAgenticLogicRequestContract(),
    sdkTypes: `${AGENTIC_LOGIC_SDK_AMBIENT_DTS}\n\n${buildAgenticLogicTypesModuleSource(
      {
        dataSurface,
        requestFieldNotes: renderAgenticLogicRequestFieldNotes(),
        sdkFieldNotes: renderAgenticLogicSdkFieldNotes(),
      }
    )}`,
    dataSurfaceEnabled: dataSurface.enabled,
  };

  return {
    entry: existingRule
      ? {
          ...base,
          existingRuleId: existingRule.ruleId,
          existingRuleIsTemplate: existingRule.managedBy !== "code",
          ejectStarterSource:
            existingRule.managedBy !== "code"
              ? AGENTIC_LOGIC_STARTER_SOURCE
              : "",
          currentName: existingRule.name,
          currentDescription: existingRule.description,
          currentSource: existingRule.draft?.source ?? "",
          currentGraphJson: existingRule.draft?.graph
            ? JSON.stringify(existingRule.draft.graph, null, 2)
            : "",
        }
      : { ...base, starterSource: AGENTIC_LOGIC_STARTER_SOURCE },
    initialInput: {
      type: "text",
      content: prompt,
      files: files && files.length > 0 ? files : undefined,
    },
  };
};

/**
 * The prose an idle-chat message opens a session with.
 *
 * Not every opening message is typed. A clicked offer — a cart to run, an
 * option chip — arrives as a `choice`, whose `value` is the instruction the
 * agent should read and whose `label` is only what the transcript shows. Both
 * kinds carry a brief, so both can start a session; returning null is reserved
 * for inputs that carry no prose at all (a bare file drop, a confirmation with
 * nothing to act on).
 */
export const readOpeningPrompt = (
  input: UserInput
): { prompt: string; files?: UserInputAttachedFile[] } | null => {
  if (input.type === "text") {
    return { prompt: input.content, files: input.files };
  }
  if (input.type === "choice") {
    return { prompt: input.choice };
  }
  return null;
};
